import React, {useEffect, useState} from "react"
import {Button, Card, DatePicker, Form, Input, PageHeader, Select, Space, Table, Tag} from "antd"
import FormItem from "antd/es/form/FormItem"
import {SearchOutlined, UndoOutlined} from "@ant-design/icons"
import {getClubComplaintList} from "../../../api/complaintManager"
import moment from "moment"
import ClubComplaintDetail from "./ClubComplaintDetail"
const { RangePicker } = DatePicker

/**
 * 过滤掉所有值为 null 或者空字符串的参数
 * @param params
 * @returns {[string, unknown][]}
 */
export const filteredParams = (params) => {
    return Object.fromEntries(
        Object.entries(params).filter(([key, value]) => value !== null && value !== '' && value !== undefined)
    )
}

/**
 * 投诉列表
 */
const ClubComplaintList = () => {

    const statusMap = {
        0: { color: "orange", label: "待处理" },
        1: { color: "blue", label: "待用户确认" },
        2: { color: "cyan", label: "平台介入" },
        3: { color: "green", label: "投诉完结" },
        4: { color: "gold", label: "待用户补充凭证" },
        5: { color: "purple", label: "待补充凭证" },
        6: { color: "red", label: "用户撤销投诉" },
        7: { color: "magenta", label: "超时关闭" }
    }
    const columns = [
        {
            title: '投诉ID',
            dataIndex: 'id',
            key: 'id',
            width: 100,
        },
        {
            title: '用户名称',
            dataIndex: 'userName',
            key: 'userName',
            width: 160,
        },
        {
            title: '流水号',
            dataIndex: 'serialNumber',
            key: 'serialNumber',
            width: 200,
            render: (text) => (
                <div style={{
                    maxWidth: 200,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                }}>{text}</div>
            )
        },
        {
            title: '手机号',
            dataIndex: 'phoneNumber',
            key: 'phoneNumber',
            width: 120
        },
        {
            title: '投诉类型',
            dataIndex: 'type',
            key: 'type',
            width: 160,
            render: (text) => (
                text === 0 ? '订单问题' : '球房服务问题'
            )
        },
        {
            title: '投诉状态',
            dataIndex: 'status',
            key: 'status',
            width: 160,
            render: (text) => (
                <Tag color={statusMap[text].color || 'default'}>{statusMap[text].label || '未知状态'}</Tag>
            )
        },
        {
            title: '投诉时间',
            dataIndex: 'createTime',
            key: 'createTime',
            width: 140,
            render: (text) => (
                moment(text).format('YYYY-MM-DD HH:mm:ss')
            )
        },
        {
            title: '处理时间',
            dataIndex: 'updateTime',
            key: 'updateTime',
            width: 140,
            render: (text) => (
                moment(text).format('YYYY-MM-DD HH:mm:ss')
            )
        },
        {
            title: '投诉内容',
            dataIndex: 'content',
            key: 'content',
            width: 240,
            render: (text) => (
                <div style={{
                    WebkitLineClamp: 3,
                    display: '-webkit-box',
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden'
                }}>
                    {text}
                </div>
            )
        },
        {
            title: '操作',
            dataIndex: 'operation',
            key: 'operation',
            width: 140,
            render: (text, record) => (
                <ClubComplaintDetail record={record}/>
            )
        },
    ]
    const [form] = Form.useForm()
    const [queryParams, setQueryParams] = useState({
        clubIds: sessionStorage.getItem('club_id'),
        type: null,
        status: null,
        phoneNumber: null,
        serialNumber: null,
        startTimestamp: null,
        endTimestamp: null,
        page: 1,
        count: 10
    })
    const [total, setTotal] = useState(0)
    const [contentList, setContentList] = useState([])
    const [searchLoading, setSearchLoading] = useState(false)
    const [resetLoading, setResetLoading] = useState(false)

    useEffect(() => {
        initForm()
    },[])

    useEffect(() => {
        handleQuery()
    }, [queryParams])

    const handleQuery = async () => {
        try {
            let params = filteredParams(queryParams)
            const res = await getClubComplaintList(params)
            setContentList(res.data.values || [])
            setTotal(res.data.total || 0)
        }catch (e) {
            console.log(e)
        }finally {
            setResetLoading(false)
            setSearchLoading(false)
        }
    }

    const handleQueryParams = async (type) => {
        if (type === 0) {
            setResetLoading(true)
            form.resetFields()
            setQueryParams({
                clubIds: sessionStorage.getItem('club_id'),
                type: null,
                status: null,
                phoneNumber: null,
                serialNumber: null,
                startTimestamp: null,
                endTimestamp: null,
                page: 1,
                count: 10
            })
        } else {
            setSearchLoading(true)
            const value = await form.getFieldsValue()
            setQueryParams(prevState => {
                return {
                    ...prevState,
                    ...value,
                    status: value.status === null ? null : [value.status],
                    startTimestamp:  value.period ? value.period[0].startOf('day').valueOf() / 1000 : null,
                    endTimestamp: value.period ? value.period[1].startOf('day').valueOf() / 1000 : null,
                    period: null,
                    page: 1,
                    count: 10
                }
            })
        }
    }

    const initForm = () => {
        form.setFieldsValue({
            type: null,
            status: null,
            phoneNumber: null,
            serialNumber: null,
            period: null
        })
    }

    return (
        <div className='club-complaint-list-page'>
            <PageHeader title={"投诉列表"}/>
            <Card bordered={false}>
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                    <Form form={form}>
                        <Space direction={"horizontal"}>
                            <FormItem label={"账单号"} name="serialNumber" style={{marginRight: 30}}>
                                <Input maxLength={50} style={{width: "220px"}}/>
                            </FormItem>
                            <FormItem label={"选择类型"} name="type" style={{marginRight: 30}}>
                                <Select options={[
                                    {
                                        value: null,
                                        label: '全部'
                                    },
                                    {
                                        value: 0,
                                        label: '订单问题'
                                    },
                                    {
                                        value: 1,
                                        label: '球房服务问题'
                                    }
                                ]} style={{width: "200px"}}/>
                            </FormItem>
                            <FormItem label={"投诉时间"} name="period">
                                <RangePicker />
                            </FormItem>
                        </Space>
                        <div>
                            <Space direction={"horizontal"}>
                                <FormItem label={"手机号"} name="phoneNumber" style={{marginRight: 30}}>
                                    <Input maxLength={11} style={{width: "220px"}}/>
                                </FormItem>
                                <FormItem label={"选择状态"} name="status" style={{marginRight: 30}}>
                                    <Select options={[
                                        {
                                            value: null,
                                            label: '全部'
                                        },
                                        ...Object.keys(statusMap).map((key) => ({
                                            value: key,
                                            label: statusMap[key].label
                                        }))
                                    ]} style={{width: "200px"}}/>
                                </FormItem>
                            </Space>
                        </div>
                    </Form>

                    <Space direction={"horizontal"}>
                        <Button type={"primary"} loading={searchLoading} disabled={resetLoading} icon={<SearchOutlined/>} onClick={() => handleQueryParams(1)}>查询</Button>
                        <Button loading={resetLoading} disabled={searchLoading} icon={<UndoOutlined/>} onClick={() => handleQueryParams(0)}>重置</Button>
                    </Space>
                </div>

                <Table columns={columns}
                       dataSource={contentList}
                       rowKey={"id"}
                       pagination={{
                           total: total,
                           defaultPageSize: 10,
                           showSizeChanger: false,
                           current: queryParams.page,
                           onChange: (page) => {
                               setQueryParams(prevState => {
                                   return {
                                       ...prevState,
                                       page: page
                                   }
                               })
                           }
                       }}
                />
            </Card>
        </div>
    )
}

export default ClubComplaintList


// WEBPACK FOOTER //
// ./src/containers/ClubComplaintManager/component/ClubComplaintList.js